import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  HStack,
  Text,
  Box,
  Icon,
  Flex,
  Collapse,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { Button } from "components/ds";
import {
  X,
  Check,
  ChevronRight,
  ChevronDown,
  UserPlus,
  Pencil,
  Flag,
  FileText,
} from "lucide-react";
import {
  useAnalyseEvidence,
  useGetEvidenceAnalysis,
  useListEvidence,
  EvidenceDiscrepancy,
  EvidenceAnalysis,
} from "state/members/accessReviewsEvidence";
import { MF_ACCESS_REVIEWS } from "state/members/accessReviews";
import { MF_OPTIONS } from "state/members/options";
import { LunaMark } from "components/ds/LunaMark";
import LunaAmbientBackground from "components/LunaAmbientBackground";
import { groupDiscrepancies } from "../reviewLogic";
import { useGovernedMotion } from "components/ds/frameGovernor";

interface LunaAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  reviewId: string;
  accountSetId: string;
  /** The review takes no more changes: Add, Update, Flag and Re-analyse are off. */
  isLocked?: boolean;
}

const DISCREPANCY_TYPE_CONFIG: Record<
  EvidenceDiscrepancy["type"],
  {
    label: string;
    tagLabel: string;
    dotColor: string;
    pillDotColor: string;
    actionLabel: string;
    actionIcon: typeof UserPlus;
  }
> = {
  user_not_in_ploy: {
    label: "Missing",
    tagLabel: "Missing user",
    dotColor: "#f59e0b",
    pillDotColor: "#5861DE",
    actionLabel: "Add user",
    actionIcon: UserPlus,
  },
  entitlement_mismatch: {
    label: "Role Mismatch",
    tagLabel: "Incorrect Entitlement",
    dotColor: "#f59e0b",
    pillDotColor: "#10b981",
    actionLabel: "Update",
    actionIcon: Pencil,
  },
  user_not_in_evidence: {
    label: "Not in evidence",
    tagLabel: "Not in evidence",
    dotColor: "#8b5cf6",
    pillDotColor: "#8b5cf6",
    actionLabel: "Flag",
    actionIcon: Flag,
  },
};

type ModalPage = "analysing" | "analysis";

const LunaAnalysisModal: React.FC<LunaAnalysisModalProps> = ({
  isOpen,
  onClose,
  reviewId,
  accountSetId,
  isLocked = false,
}) => {
  const toast = useToast();
  const analyseEvidence = useAnalyseEvidence();
  const { evidenceFiles } = useListEvidence(reviewId);
  const { analysis: existingAnalysis, isPending } =
    useGetEvidenceAnalysis(accountSetId);
  const options = MF_OPTIONS.useListOptions();
  const { mutate: reviewAccounts, isLoading: isReviewingAccounts } =
    MF_ACCESS_REVIEWS.useReviewCampaignAccounts(reviewId);
  const { mutate: addAccount, isLoading: isAddingAccount } =
    MF_ACCESS_REVIEWS.useAddAccountToCampaignReview();
  const updateEntitlements = MF_ACCESS_REVIEWS.useUpdateAccountEntitlements();
  const { data: reviewEntitlements } =
    MF_ACCESS_REVIEWS.useGetReviewEntitlements(reviewId);
  const accountRows = MF_ACCESS_REVIEWS.useListReviewAccountRows(reviewId);

  const [page, setPage] = useState<ModalPage>("analysis");
  const [actionedIndices, setActionedIndices] = useState<Set<number>>(
    new Set(),
  );
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const dismissStorageKey = `luna-dismissed-${reviewId}`;

  const [dismissedIndices, setDismissedIndices] = useState<Set<number>>(() => {
    try {
      const stored = localStorage.getItem(dismissStorageKey);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const handleDismiss = useCallback(
    (idx: number) => {
      setDismissedIndices((prev) => {
        const next = new Set(prev).add(idx);
        localStorage.setItem(dismissStorageKey, JSON.stringify([...next]));
        return next;
      });
    },
    [dismissStorageKey],
  );

  const analysisResult =
    existingAnalysis?.status === "pending" ||
    existingAnalysis?.status === "failed"
      ? null
      : existingAnalysis;

  const allAccounts = useMemo(() => accountRows ?? [], [accountRows]);

  const alreadyResolvedIndices = useMemo(() => {
    if (!analysisResult || allAccounts.length === 0) return new Set<number>();
    const resolved = new Set<number>();

    analysisResult.discrepancies.forEach((d, idx) => {
      switch (d.type) {
        case "user_not_in_evidence": {
          if (!d.account_id) break;
          const account = allAccounts.find((a: any) => a.id === d.account_id);
          if (
            account &&
            (["removed", "requires_removal", "already_removed"].includes(
              account.status,
            ) ||
              account.status === "complete")
          ) {
            resolved.add(idx);
          }
          break;
        }
        case "user_not_in_ploy": {
          const email = d.account_identifier || d.evidence_user?.identifier;
          if (!email) break;
          const found = allAccounts.some(
            (a: any) => a.member?.email?.toLowerCase() === email.toLowerCase(),
          );
          if (found) resolved.add(idx);
          break;
        }
        case "entitlement_mismatch": {
          if (!d.account_id) break;
          const account = allAccounts.find((a: any) => a.id === d.account_id);
          if (!account?.entitlements?.length) break;
          const currentNames = new Set(
            account.entitlements.map((e: any) =>
              e.snapshotEntitlementName?.toLowerCase(),
            ),
          );
          const evidenceNames = new Set(
            (d.evidence_entitlements || []).map((n: string) => n.toLowerCase()),
          );
          // Only resolved if entitlements match exactly (same set both directions)
          if (
            evidenceNames.size > 0 &&
            evidenceNames.size === currentNames.size &&
            [...evidenceNames].every((n) => currentNames.has(n))
          ) {
            resolved.add(idx);
          }
          break;
        }
      }
    });

    return resolved;
  }, [analysisResult, allAccounts]);

  const effectiveResolvedIndices = useMemo(() => {
    const merged = new Set(alreadyResolvedIndices);
    actionedIndices.forEach((idx) => merged.add(idx));
    dismissedIndices.forEach((idx) => merged.add(idx));
    return merged;
  }, [alreadyResolvedIndices, actionedIndices, dismissedIndices]);

  const handleAnalyse = useCallback(async () => {
    if (isLocked) return;
    setActionedIndices(new Set());
    setDismissedIndices(new Set());
    localStorage.removeItem(dismissStorageKey);
    setExpandedIdx(null);
    setPage("analysing");
    try {
      await analyseEvidence.mutateAsync({ accountSetId });
      // Don't transition page — polling will detect completion
    } catch (error) {
      onClose();
      toast({
        title: "Analysis failed",
        description:
          error instanceof Error ? error.message : "Failed to analyse evidence",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [
    isLocked,
    analyseEvidence,
    accountSetId,
    onClose,
    toast,
    dismissStorageKey,
  ]);

  // On open: if no existing analysis, trigger analysis immediately
  useEffect(() => {
    if (isOpen && !existingAnalysis) {
      handleAnalyse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // When opening with existing analysis, determine initial page
  useEffect(() => {
    if (isOpen && existingAnalysis) {
      if (existingAnalysis.status === "pending") {
        setPage("analysing");
      } else {
        setPage("analysis");
      }
    }
  }, [isOpen, existingAnalysis]);

  // Transition from analysing to results when polling detects completion
  useEffect(() => {
    if (
      page === "analysing" &&
      existingAnalysis &&
      existingAnalysis.status !== "pending"
    ) {
      if (existingAnalysis.status === "failed") {
        onClose();
        toast({
          title: "Analysis failed",
          description:
            (existingAnalysis as any).error || "Evidence analysis failed",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        setPage("analysis");
      }
    }
  }, [page, existingAnalysis, onClose, toast]);

  const handleDiscrepancyAction = useCallback(
    async (discrepancy: EvidenceDiscrepancy, idx: number) => {
      if (isLocked) return;
      try {
        switch (discrepancy.suggested_action) {
          case "flag_for_removal": {
            if (!discrepancy.account_id) {
              toast({
                title: "Cannot flag",
                description: "No account ID associated with this discrepancy",
                status: "warning",
                duration: 3000,
                isClosable: true,
              });
              return;
            }
            await reviewAccounts({
              reviewId,
              accountIds: [discrepancy.account_id],
              review: "not_required",
              markAsRemoved: true,
              note: `Flagged by Luna: ${discrepancy.reasoning}`,
            });
            break;
          }
          case "update_entitlements": {
            if (!discrepancy.account_id) {
              toast({
                title: "Cannot update",
                description: "No account ID associated with this discrepancy",
                status: "warning",
                duration: 3000,
                isClosable: true,
              });
              return;
            }
            const evidenceNames = discrepancy.evidence_entitlements || [];
            const matched = reviewEntitlements.filter((e) =>
              evidenceNames.some(
                (name) => e.name.toLowerCase() === name.toLowerCase(),
              ),
            );
            const matchedIds = matched.map((e) => e.id);
            const matchedNamesLower = new Set(
              matched.map((e) => e.name.toLowerCase()),
            );
            const newEntitlementNames = evidenceNames.filter(
              (name) => !matchedNamesLower.has(name.toLowerCase()),
            );

            if (matchedIds.length === 0 && newEntitlementNames.length === 0) {
              toast({
                title: "No matching entitlements",
                description: `Could not match evidence entitlements (${evidenceNames.join(", ")}) to available entitlements for this resource.`,
                status: "warning",
                duration: 5000,
                isClosable: true,
              });
              return;
            }

            await updateEntitlements.mutate(
              discrepancy.account_id,
              matchedIds,
              newEntitlementNames,
            );
            break;
          }
          case "add_user_to_resource": {
            const email =
              discrepancy.account_identifier ||
              discrepancy.evidence_user?.identifier;
            const member = email
              ? options?.members?.find(
                  (m) =>
                    (m as any).subtitle?.toLowerCase() === email.toLowerCase(),
                )
              : null;

            if (!member) {
              toast({
                title: "Member not found",
                description: `No member found with email ${email || "unknown"}. They may need to be added to your organisation first.`,
                status: "warning",
                duration: 5000,
                isClosable: true,
              });
              return;
            }

            const evidenceEntitlementNames = discrepancy.evidence_user?.role
              ? [discrepancy.evidence_user.role]
              : discrepancy.evidence_entitlements || [];

            // Match entitlement names to IDs, create new ones for unmatched
            const addMatched = reviewEntitlements.filter((e) =>
              evidenceEntitlementNames.some(
                (name) => e.name.toLowerCase() === name.toLowerCase(),
              ),
            );
            const addMatchedIds = addMatched.map((e) => e.id);
            const addMatchedNamesLower = new Set(
              addMatched.map((e) => e.name.toLowerCase()),
            );
            const addNewEntitlementNames = evidenceEntitlementNames.filter(
              (name) => !addMatchedNamesLower.has(name.toLowerCase()),
            );

            await addAccount({
              reviewId,
              accountId: member.value,
              entitlementIds: addMatchedIds,
              newEntitlementNames:
                addNewEntitlementNames.length > 0
                  ? addNewEntitlementNames
                  : undefined,
            });
            break;
          }
        }

        setActionedIndices((prev) => new Set(prev).add(idx));
        toast({
          title: "Action applied",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: "Action failed",
          description:
            error instanceof Error ? error.message : "Failed to apply action",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [
      isLocked,
      toast,
      reviewAccounts,
      reviewId,
      reviewEntitlements,
      updateEntitlements,
      options,
      addAccount,
    ],
  );

  const handleClose = () => {
    setExpandedIdx(null);
    onClose();
  };

  // id -> filename, so a discrepancy's source_evidence_file_id can be shown on
  // hover (PLO-4818, item 3). The API exposes only the file id (no per-line
  // citation text); if the id is absent or unknown the source badge is hidden.
  const evidenceFileNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const f of evidenceFiles) map[f.id] = f.filename;
    return map;
  }, [evidenceFiles]);

  // Bulk-apply the per-type action to every still-unresolved discrepancy in a
  // group (PLO-4818, item 3). Reuses the exact same per-discrepancy handler so
  // grouped actions and single actions stay behaviourally identical.
  const handleGroupAction = useCallback(
    async (items: { discrepancy: EvidenceDiscrepancy; index: number }[]) => {
      if (isLocked) return;
      for (const { discrepancy, index } of items) {
        if (effectiveResolvedIndices.has(index)) continue;
        // eslint-disable-next-line no-await-in-loop
        await handleDiscrepancyAction(discrepancy, index);
      }
    },
    // handleDiscrepancyAction is memoized on its own inputs; include it so a
    // bulk apply always runs the freshest per-discrepancy handler, and capture
    // the current resolved set so we skip anything already handled.
    [isLocked, effectiveResolvedIndices, handleDiscrepancyAction],
  );

  const discrepancyCounts = useMemo(() => {
    if (!analysisResult)
      return { total: 0, byType: {} as Record<string, number> };
    const byType: Record<string, number> = {};
    for (const d of analysisResult.discrepancies) {
      byType[d.type] = (byType[d.type] || 0) + 1;
    }
    return { total: analysisResult.discrepancies.length, byType };
  }, [analysisResult]);

  const isAnalysisPage = page === "analysis" && analysisResult;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="4xl"
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.300" />
      <ModalContent borderRadius="24px" maxH="90vh" overflow="hidden">
        {/* Header */}
        <HStack
          px={6}
          py={5}
          borderBottom="1px solid"
          borderColor="#e3eaec"
          justify="space-between"
          flexShrink={0}
        >
          <Text fontSize="16px" fontWeight="600" color="#1d2a33">
            Discrepancies
          </Text>
          <Button
            aria-label="Close"
            icon={<Icon as={X} boxSize={4} />}
            size="sm"
            variant="subtle"
            tone="neutral"
            onClick={handleClose}
          />
        </HStack>

        <ModalBody p={0} overflowY="auto">
          {page === "analysing" ? (
            <AnalysingPage fileCount={evidenceFiles.length} />
          ) : isAnalysisPage ? (
            <TriagePage
              analysisResult={analysisResult!}
              discrepancyCounts={discrepancyCounts}
              resolvedIndices={effectiveResolvedIndices}
              expandedIdx={expandedIdx}
              isLocked={isLocked}
              isActioning={
                isReviewingAccounts ||
                isAddingAccount ||
                updateEntitlements.isLoading
              }
              evidenceFileNames={evidenceFileNames}
              onAction={handleDiscrepancyAction}
              onActionGroup={handleGroupAction}
              onDismiss={handleDismiss}
              onToggleExpand={(idx) =>
                setExpandedIdx(expandedIdx === idx ? null : idx)
              }
            />
          ) : null}
        </ModalBody>

        {/* Footer */}
        {isAnalysisPage && (
          <HStack px={6} py={5} justify="flex-end" spacing={4} flexShrink={0}>
            <Button
              size="sm"
              variant="subtle"
              tone="neutral"
              onClick={handleClose}
            >
              Close
            </Button>
            <Button
              onClick={handleAnalyse}
              isLoading={analyseEvidence.isLoading}
              isDisabled={isLocked}
              leadingIcon={
                <LunaMark size={18} state={analyseEvidence.isLoading ? "thinking" : "idle"} animated={analyseEvidence.isLoading} />
              }
              size="sm"
              variant="outline"
              tone="luna"
            >
              Re-analyse
            </Button>
          </HStack>
        )}
      </ModalContent>
    </Modal>
  );
};

/* ─── Analysing Page ─── */

const AnalysingPage: React.FC<{ fileCount: number }> = ({ fileCount }) => {
  /* the parsing pulse free-runs for the whole analysis — govern it. Scoped
     to the pulse row only: the mark and the ambient backdrop register
     themselves, and tightly scoped roots keep each motion on its own tier. */
  const pulseRef = useGovernedMotion<HTMLDivElement>();
  return (
  <Flex
    direction="column"
    align="center"
    justify="center"
    minH="500px"
    position="relative"
    overflow="hidden"
  >
    {/* Subtle Luna blur-blob backdrop — the same drifting-blob treatment the
        Luna setup flow uses behind its icon while scanning (PLO-4818, item 3). */}
    <LunaAmbientBackground />

    <VStack spacing={4} zIndex={1}>
      <LunaMark size={48} state="thinking" />
      <VStack spacing={2}>
        <Text fontSize="24px" fontWeight="600" color="#1d2a33">
          Luna is reading your evidence
        </Text>
        <Text fontSize="14px" color="#5a6c75">
          Comparing against tracked accounts in this access review
        </Text>
      </VStack>
      <HStack ref={pulseRef} spacing={2}>
        <Box w="10px" h="10px" borderRadius="full" bg="#5861DE" opacity={0.6}>
          <Box
            w="full"
            h="full"
            borderRadius="full"
            bg="#5861DE"
            animation="pulse 1.5s ease-in-out infinite"
            sx={{
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1, transform: "scale(1)" },
                "50%": { opacity: 0.4, transform: "scale(0.7)" },
              },
              /* the governor skips reduced-motion, so the pulse must not
                 free-run there either; the dot stays visible, just still */
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          />
        </Box>
        <Text fontSize="14px" fontWeight="600" color="#5861DE">
          Parsing {fileCount} file{fileCount !== 1 ? "s" : ""}
        </Text>
      </HStack>
    </VStack>
  </Flex>
  );
};

/* ─── Triage Page ─── */

interface TriagePageProps {
  analysisResult: EvidenceAnalysis;
  discrepancyCounts: { total: number; byType: Record<string, number> };
  resolvedIndices: Set<number>;
  expandedIdx: number | null;
  isLocked: boolean;
  isActioning: boolean;
  evidenceFileNames: Record<string, string>;
  onAction: (discrepancy: EvidenceDiscrepancy, idx: number) => void;
  onActionGroup: (
    items: { discrepancy: EvidenceDiscrepancy; index: number }[],
  ) => void;
  onDismiss: (idx: number) => void;
  onToggleExpand: (idx: number) => void;
}

const TriagePage: React.FC<TriagePageProps> = ({
  analysisResult,
  discrepancyCounts,
  resolvedIndices,
  expandedIdx,
  isLocked,
  isActioning,
  evidenceFileNames,
  onAction,
  onActionGroup,
  onDismiss,
  onToggleExpand,
}) => {
  const hasDiscrepancies = analysisResult.discrepancies.length > 0;

  // Group by type, preserving each discrepancy's ORIGINAL flat index (all the
  // resolved/dismissed/expanded bookkeeping keys on that index). reviewLogic's
  // groupDiscrepancies gives the stable type order + labels.
  const indexed = analysisResult.discrepancies.map((discrepancy, index) => ({
    discrepancy,
    index,
    type: discrepancy.type,
  }));
  const groups = groupDiscrepancies(indexed);

  return (
    <VStack spacing={6} align="stretch" p={6}>
      {/* Summary card */}
      <Box
        p={6}
        borderRadius="16px"
        boxShadow="0px 0px 1px 0px rgba(0,0,0,0.32), 0px 0px 2px 0px rgba(0,0,0,0.08), 0px 1px 3px 0px rgba(0,0,0,0.08)"
        background={
          hasDiscrepancies
            ? "linear-gradient(-76deg, #ffffff 2%, #FFF0D5 96%)"
            : "linear-gradient(-76deg, #ffffff 2%, #D5FFE8 96%)"
        }
      >
        <VStack spacing={4} align="start">
          <VStack spacing={2} align="start">
            <Text fontSize="16px" fontWeight="600" color="#1d2a33">
              {hasDiscrepancies
                ? `${discrepancyCounts.total} Discrepanc${discrepancyCounts.total === 1 ? "y" : "ies"} found`
                : "No discrepancies found"}
            </Text>
            <Text fontSize="14px" fontWeight="500" color="#5a6c75">
              {analysisResult.summary}
            </Text>
          </VStack>

          {hasDiscrepancies && (
            <HStack spacing={2} flexWrap="wrap">
              {Object.entries(discrepancyCounts.byType).map(([type, count]) => {
                const config =
                  DISCREPANCY_TYPE_CONFIG[type as EvidenceDiscrepancy["type"]];
                if (!config) return null;
                return (
                  <HStack
                    key={type}
                    bg="white"
                    border="1px solid"
                    borderColor="#e3eaec"
                    borderRadius="8px"
                    px={2}
                    py={1.5}
                    spacing={1}
                  >
                    <Box
                      w="8px"
                      h="8px"
                      borderRadius="full"
                      bg={config.pillDotColor}
                      flexShrink={0}
                    />
                    <Text fontSize="14px" fontWeight="500" color="#1d2a33">
                      {config.label}
                    </Text>
                    <Text fontSize="14px" fontWeight="600" color="#7a959f">
                      &middot;
                    </Text>
                    <Text fontSize="14px" fontWeight="600" color="#1d2a33">
                      {count}
                    </Text>
                  </HStack>
                );
              })}
            </HStack>
          )}
        </VStack>
      </Box>

      {/* Discrepancy list, grouped by type with a bulk action per group. */}
      {hasDiscrepancies && (
        <VStack spacing={6} align="stretch">
          {groups.map((group) => {
            const config =
              DISCREPANCY_TYPE_CONFIG[
                group.type as EvidenceDiscrepancy["type"]
              ];
            const unresolved = group.items.filter(
              (it) => !resolvedIndices.has(it.index),
            );
            return (
              <VStack key={group.type} spacing={3} align="stretch">
                {/* group header + bulk action */}
                <HStack justify="space-between" align="center">
                  <HStack spacing={2}>
                    {config && (
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg={config.pillDotColor}
                        flexShrink={0}
                      />
                    )}
                    <Text fontSize="14px" fontWeight="600" color="#1d2a33">
                      {group.label}
                    </Text>
                    <Text fontSize="14px" fontWeight="600" color="#7a959f">
                      &middot;
                    </Text>
                    <Text fontSize="14px" fontWeight="600" color="#1d2a33">
                      {group.items.length}
                    </Text>
                  </HStack>
                  {config && unresolved.length > 0 && (
                    <HStack
                      as="button"
                      bg="white"
                      borderRadius="6px"
                      px={3}
                      py={1}
                      spacing={1.5}
                      boxShadow="0px 0px 1px 0px rgba(0,0,0,0.32), 0px 0px 2px 0px rgba(0,0,0,0.08), 0px 1px 3px 0px rgba(0,0,0,0.08)"
                      _hover={{ bg: "#f3f7f8" }}
                      onClick={() =>
                        onActionGroup(
                          unresolved.map((it) => ({
                            discrepancy: it.discrepancy,
                            index: it.index,
                          })),
                        )
                      }
                      disabled={isLocked}
                      cursor={isLocked ? "not-allowed" : "pointer"}
                      opacity={isActioning || isLocked ? 0.6 : 1}
                    >
                      <Icon
                        as={config.actionIcon}
                        boxSize={4}
                        color="#1d2a33"
                      />
                      <Text fontSize="12px" fontWeight="600" color="#1d2a33">
                        {config.actionLabel} all ({unresolved.length})
                      </Text>
                    </HStack>
                  )}
                </HStack>

                {group.items.map((it) => (
                  <DiscrepancyRow
                    key={it.index}
                    discrepancy={it.discrepancy}
                    idx={it.index}
                    isExpanded={expandedIdx === it.index}
                    isResolved={resolvedIndices.has(it.index)}
                    isLocked={isLocked}
                    isActioning={isActioning}
                    sourceFileName={
                      it.discrepancy.source_evidence_file_id
                        ? evidenceFileNames[
                            it.discrepancy.source_evidence_file_id
                          ]
                        : undefined
                    }
                    onAction={() => onAction(it.discrepancy, it.index)}
                    onDismiss={() => onDismiss(it.index)}
                    onToggle={() => onToggleExpand(it.index)}
                  />
                ))}
              </VStack>
            );
          })}
        </VStack>
      )}
    </VStack>
  );
};

/* ─── Discrepancy Row ─── */

interface DiscrepancyRowProps {
  discrepancy: EvidenceDiscrepancy;
  idx: number;
  isExpanded: boolean;
  isResolved: boolean;
  isLocked: boolean;
  isActioning: boolean;
  /** filename of the evidence file this discrepancy was sourced from, if known */
  sourceFileName?: string;
  onAction: () => void;
  onDismiss: () => void;
  onToggle: () => void;
}

const DiscrepancyRow: React.FC<DiscrepancyRowProps> = ({
  discrepancy: d,
  isExpanded,
  isResolved,
  isLocked,
  isActioning,
  sourceFileName,
  onAction,
  onDismiss,
  onToggle,
}) => {
  const config =
    DISCREPANCY_TYPE_CONFIG[d.type] ||
    DISCREPANCY_TYPE_CONFIG.user_not_in_evidence;
  const name =
    d.account_identifier ||
    d.evidence_user?.name ||
    d.evidence_user?.identifier ||
    "Unknown";
  const email = d.evidence_user?.identifier || d.account_identifier || "";
  const role = d.evidence_user?.role;

  return (
    <Box
      bg="white"
      borderRadius="8px"
      border={isExpanded ? "none" : "1px solid"}
      borderColor="#e3eaec"
      boxShadow={
        isExpanded
          ? "0px 0px 1px 0px rgba(0,0,0,0.32), 0px 0px 2px 0px rgba(0,0,0,0.08), 0px 1px 3px 0px rgba(0,0,0,0.08)"
          : "none"
      }
      opacity={isResolved ? 0.5 : 1}
      transition="all 0.15s"
      overflow="hidden"
    >
      {/* Collapsed row */}
      <HStack
        px={4}
        py={4}
        justify="space-between"
        cursor="pointer"
        onClick={onToggle}
        _hover={{ bg: isExpanded ? undefined : "#fafcfc" }}
      >
        <HStack spacing={3} flex={1} minW={0}>
          {/* Type tag */}
          <HStack
            border="1px solid"
            borderColor="#d1dce0"
            borderRadius="6px"
            px={1.5}
            py={1}
            spacing={1.5}
            flexShrink={0}
          >
            <Box w="8px" h="8px" borderRadius="full" bg={config.dotColor} />
            <Text
              fontSize="12px"
              fontWeight="500"
              color="#1d2a33"
              whiteSpace="nowrap"
            >
              {config.tagLabel}
            </Text>
          </HStack>

          {/* Name + email */}
          <VStack align="start" spacing={0.5} minW={0}>
            <Text
              fontSize="14px"
              fontWeight="500"
              color="#1d2a33"
              noOfLines={1}
            >
              {name}
            </Text>
            {email && email !== name && (
              <Text
                fontSize="12px"
                fontWeight="500"
                color="#5a6c75"
                noOfLines={1}
              >
                {email}
              </Text>
            )}
          </VStack>

          {/* Evidence source — the file this discrepancy came from, viewable on
              hover. Only shown when the API supplied a resolvable file id. */}
          {sourceFileName && (
            <Tooltip
              label={`From evidence: ${sourceFileName}`}
              placement="top"
              hasArrow
              openDelay={200}
            >
              <Box
                as="span"
                display="inline-flex"
                alignItems="center"
                flexShrink={0}
                color="#7a959f"
                onClick={(e) => e.stopPropagation()}
                aria-label={`Source evidence: ${sourceFileName}`}
              >
                <Icon as={FileText} boxSize={4} />
              </Box>
            </Tooltip>
          )}
        </HStack>

        {/* Entitlement mismatch tags */}
        {d.type === "entitlement_mismatch" && (
          <HStack
            spacing={2}
            flexShrink={0}
            display={{ base: "none", md: "flex" }}
          >
            <HStack
              bg="#eef4f5"
              borderRadius="6px"
              px={1.5}
              py={1}
              spacing={1.5}
            >
              <Box w="8px" h="8px" borderRadius="full" bg="#f59e0b" />
              <Text fontSize="12px" fontWeight="500" color="#5a6c75">
                Current:
              </Text>
              <Text fontSize="12px" fontWeight="500" color="#1d2a33">
                {d.ploy_entitlements?.join(", ") || "none"}
              </Text>
            </HStack>
            <Text fontSize="12px" color="#5a6c75">
              &rarr;
            </Text>
            {d.evidence_entitlements && d.evidence_entitlements.length > 0 && (
              <HStack
                bg="#eef4f5"
                borderRadius="6px"
                px={1.5}
                py={1}
                spacing={1.5}
              >
                <Box w="8px" h="8px" borderRadius="full" bg="#10b981" />
                <Text fontSize="12px" fontWeight="500" color="#5a6c75">
                  Correct:
                </Text>
                <Text fontSize="12px" fontWeight="500" color="#1d2a33">
                  {d.evidence_entitlements.join(", ")}
                </Text>
              </HStack>
            )}
          </HStack>
        )}

        {/* Action + chevron */}
        <HStack spacing={2} flexShrink={0} ml={4}>
          {!isResolved && (
            <HStack
              as="button"
              bg="white"
              borderRadius="6px"
              px={3}
              py={1}
              spacing={1.5}
              boxShadow="0px 0px 1px 0px rgba(0,0,0,0.32), 0px 0px 2px 0px rgba(0,0,0,0.08), 0px 1px 3px 0px rgba(0,0,0,0.08)"
              _hover={{ bg: "#f3f7f8" }}
              onClick={(e) => {
                e.stopPropagation();
                onAction();
              }}
              disabled={isLocked}
              cursor={isLocked ? "not-allowed" : "pointer"}
              opacity={isActioning || isLocked ? 0.6 : 1}
            >
              <Icon as={config.actionIcon} boxSize={4} color="#1d2a33" />
              <Text fontSize="12px" fontWeight="600" color="#1d2a33">
                {config.actionLabel}
              </Text>
            </HStack>
          )}
          {isResolved && (
            <HStack spacing={1}>
              <Icon as={Check} boxSize={4} color="#047857" />
              <Text fontSize="12px" fontWeight="600" color="#047857">
                Done
              </Text>
            </HStack>
          )}
          <Icon
            as={isExpanded ? ChevronDown : ChevronRight}
            boxSize={4}
            color="#5a6c75"
          />
        </HStack>
      </HStack>

      {/* Expanded detail */}
      <Collapse in={isExpanded} animateOpacity>
        <VStack align="start" spacing={2} px={4} pb={4}>
          {/* User details line */}
          <HStack
            spacing={1}
            fontSize="14px"
            fontWeight="500"
            color="#5a6c75"
            flexWrap="wrap"
          >
            {name && <Text>{name}</Text>}
            {email && email !== name && (
              <>
                <Text>&middot;</Text>
                <Text>{email}</Text>
              </>
            )}
            {role && (
              <>
                <Text>&middot;</Text>
                <Text>{role}</Text>
              </>
            )}
          </HStack>

          {/* Reasoning */}
          <Text fontSize="14px" color="#1d2a33" lineHeight="1.5">
            {d.reasoning}
          </Text>

          {!isResolved && (
            <Button
              size="xs"
              variant="subtle"
              tone="neutral"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss();
              }}
              style={{ marginTop: "4px" }}
            >
              Dismiss
            </Button>
          )}
        </VStack>
      </Collapse>
    </Box>
  );
};

export default LunaAnalysisModal;
